import {Component, Input, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatDatepickerInputEvent} from '@angular/material/datepicker';
import {Operand, OperatorType} from '@em/shared/mapping-engine/domain-models';
import {EmMaterialWrapperComponent} from '@em/shared/ui';
import {merge, Observable, Subject} from 'rxjs';
import {filter, map, shareReplay, startWith, tap} from 'rxjs/operators';

@Component({
  selector: 'em-select-operand',
  templateUrl: './select-operand.component.html',
  styleUrls: ['./select-operand.component.scss'],
  standalone: false,
})
export class EmSelectOperandComponent
  extends EmMaterialWrapperComponent<Operand | null>
  implements OnInit
{
  @Input() set availableAttributes(value: string[] | undefined | null) {
    if (value) {
      this.availableOperands = value.map(
        (attribute) =>
          ({
            type: 'attribute',
            value: attribute,
          }) as Operand,
      );
    } else {
      this.availableOperands = value;
    }
  }
  // To show custom attribute in different group/icon
  @Input() customAttributes: string[] | undefined | null;
  @Input() allowStaticValues: boolean | undefined;
  @Input() staticValuesOnly: boolean | undefined;
  @Input() hidePrefix: boolean | undefined;
  @Input() type: OperatorType | null | undefined = 'string';

  readonly inputControl = new FormControl<
    string | number | boolean | undefined
  >('');
  readonly inputFocused = new Subject<void>();
  availableOperands: Operand[] | null | undefined = [];
  customOperands: Operand[] | null | undefined = [];

  filteredProperties$: Observable<Operand[]> | undefined;
  filteredPredefinedProperties$: Observable<Operand[]> | undefined;
  filteredCustomProperties$: Observable<Operand[] | null> | undefined;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.filteredProperties$ = merge(
      this.inputControl.valueChanges,
      this.inputFocused.asObservable(),
    ).pipe(
      startWith(undefined),
      // not needed for the boolean type as we use a select
      filter(() => this.type !== 'boolean' && this.type !== 'array'),
      filter(
        (value) =>
          !value || typeof value === 'string' || typeof value === 'number',
      ),
      tap((value) => {
        if (this.type === 'integer' && value !== undefined) {
          if (value === null) {
            this.controlValue = null;
          } else {
            this.controlValue = {
              type: 'static_value',
              value: value || 0,
            } as Operand;
          }
          return this.controlValueChanged(this.controlValue);
        }
        if (value !== null && value !== undefined) {
          if (value === '' || !this.allowStaticValues) {
            this.controlValue = null;
          } else {
            this.controlValue = {
              type: 'static_value',
              value: value || '',
            } as Operand;
          }

          this.controlValueChanged(this.controlValue);
        }
      }),
      map((value) => {
        if (this.staticValuesOnly) {
          return [];
        } else {
          return this._getFilteredProperties(value?.toString());
        }
      }),
      shareReplay({bufferSize: 1, refCount: false}),
    );

    this.filteredPredefinedProperties$ = this.filteredProperties$.pipe(
      map((all) =>
        all.filter(
          (attr) =>
            attr.value &&
            (!this.customAttributes ||
              this.customAttributes?.indexOf(attr.value?.toString()) === -1),
        ),
      ),
    );

    this.filteredCustomProperties$ = this.filteredProperties$.pipe(
      map((all) =>
        all.filter(
          (attr) =>
            attr.value &&
            this.customAttributes &&
            this.customAttributes?.indexOf(attr.value?.toString()) > -1,
        ),
      ),
      map((custom) => (custom.length ? custom : null)),
    );
  }

  override writeValue(operand: Operand): void {
    this.controlValue = operand;
    if (operand?.value !== undefined && operand?.value !== null) {
      this.inputControl.setValue(operand.value, {emitEvent: false});
    }
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedOperand = event.option.value as Operand;

    this.controlValue = selectedOperand;
    this.inputControl.setValue(selectedOperand.value, {emitEvent: false});
    this.controlValueChanged(this.controlValue);
  }

  compareWith(a?: Operand, b?: Operand) {
    return a?.type === b?.type && a?.value === b?.value;
  }

  dateChanged(event: MatDatepickerInputEvent<unknown, unknown>) {
    if (event.value) {
      this.controlValue = {
        type: 'static_value',
        value: event.value,
      } as Operand;
    } else {
      this.controlValue = null;
    }

    this.controlValueChanged(this.controlValue);
  }

  booleanChange(event: boolean) {
    this.controlValue = {
      type: 'static_value',
      value: event,
    } as Operand;

    this.controlValueChanged(this.controlValue);
  }

  inputBlur() {
    // if input blur but controlValue is not set
    // try to find an operand that match the input value, and use it as the controlValue
    if (!this.controlValue && this.inputControl.value) {
      const matchOperand = this.availableOperands?.find(
        (op) => op.value === this.inputControl.value,
      );
      if (matchOperand) {
        this.controlValue = matchOperand;
        this.controlValueChanged(this.controlValue);
      } else {
        this.inputControl.setValue('', {emitEvent: false});
      }
    }

    this.controlTouched();
  }

  isCustom(attr: string | number | boolean | undefined): boolean {
    return !!attr && !!this.customAttributes?.includes(attr?.toString());
  }

  private _getFilteredProperties(
    value: string | number | undefined,
  ): Operand[] {
    if (!value) {
      return this.availableOperands || [];
    }

    const filteredAttributes =
      this.availableOperands?.filter((prop) =>
        prop?.value
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLocaleLowerCase()),
      ) ||
      this.availableOperands ||
      [];

    // Add user input as static value at the top of the list
    // if no available attributes passed don't add static value, no confusion here
    if (this.allowStaticValues && this.availableAttributes?.length) {
      return [{type: 'static_value', value}, ...filteredAttributes];
    } else {
      return [...filteredAttributes];
    }
  }
}
